import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table, Space, Select, Button, Tag, message, Input } from 'antd';
import request from '../../../utils/request';

const statusColor = (s: string) => ({
  pending: 'default',
  accepted: 'processing',
  in_progress: 'blue',
  completed: 'green',
  rejected: 'red',
  cancelled: 'default'
} as any)[s] || 'default';
const statusText = (s: string) => ({
  pending: '待领取',
  accepted: '已领取',
  in_progress: '执行中',
  completed: '已完成',
  rejected: '已驳回',
  cancelled: '已取消'
} as any)[s] || s || '-';

const InternalWorkOrderListPage: React.FC = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<{ status?: string; warehouse_id?: number; priority?: string; keyword?: string }>({});
  const [warehouseOptions, setWarehouseOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [whLoading, setWhLoading] = useState(false);

  const loadWarehouses = async () => {
    setWhLoading(true);
    try {
      const resp = await request.get('/api/v1/warehouse/warehouses/', { params: { page_size: 200 } });
      const rows = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : []);
      setWarehouseOptions(rows.map((w:any)=> ({ value: Number(w.id), label: `${w.name || ''} · ${w.code || w.id}` })));
    } catch { setWarehouseOptions([]); } finally { setWhLoading(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params:any = { category: 'internal', page_size: 200 };
      if (filters.status) params.status = filters.status;
      if (filters.warehouse_id) params.warehouse_id = filters.warehouse_id;
      if (filters.priority) params.priority = filters.priority;
      // 关键字（简单匹配 system_code / remarks）
      const resp = await request.get('/api/v1/workorders/workorders/', { params });
      const data = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : []);
      let rows = data;
      if (filters.keyword && String(filters.keyword).trim() !== '') {
        const kw = String(filters.keyword).trim().toLowerCase();
        rows = rows.filter((r:any)=> String(r?.system_code||'').toLowerCase().includes(kw) || String(r?.remarks||'').toLowerCase().includes(kw));
      }
      setRows(rows);
    } catch {
      message.error('加载失败');
    } finally { setLoading(false); }
  };

  useEffect(() => { loadWarehouses(); }, []);
  useEffect(() => { load(); }, [filters]);

  const accept = async (id:number) => {
    try { await request.post(`/api/v1/workorders/workorders/${id}/accept/`, {}); message.success('已领取'); load(); } catch { message.error('操作失败'); }
  };
  const start = async (id:number) => {
    try { await request.post(`/api/v1/workorders/workorders/${id}/start/`, {}); message.success('已开始'); load(); } catch { message.error('操作失败'); }
  };
  const complete = async (id:number) => {
    try { await request.post(`/api/v1/workorders/workorders/${id}/complete/`, {}); message.success('已完成'); load(); } catch { message.error('操作失败'); }
  };

  const columns = useMemo(() => [
    { title: '系统编码', dataIndex: 'system_code', width: 180 },
    { title: '状态', dataIndex: 'status', width: 120, render: (v:string)=> <Tag color={statusColor(v)}>{statusText(v)}</Tag> },
    { title: '优先级', dataIndex: 'priority', width: 120, render: (v:string)=> <Tag color={v==='super'?'red':(v==='high'?'orange':'default')}>{v || '-'}</Tag> },
    { title: '仓库', dataIndex: 'warehouse_id', width: 220, render: (v:any, r:any)=> {
      const opt = warehouseOptions.find(o=> Number(o.value)===Number(r.warehouse_id));
      return opt ? opt.label : r.warehouse_id || '-';
    }},
    { title: '模板', dataIndex: 'template_snapshot', width: 280, render: (v:any)=> {
      const name = v?.name || v?.code || '-';
      const code = v?.code || '';
      return `${name}${code ? ' · '+code : ''}`;
    }},
    { title: '备注', dataIndex: 'remarks', ellipsis: true },
    { title: '操作', key: 'ops', width: 220, render: (_:any, r:any) => {
      const s = String(r.status);
      return (
        <Space>
          {s === 'pending' ? <Button size="small" onClick={()=>accept(Number(r.id))}>领取</Button> : null}
          {s === 'accepted' ? <Button size="small" onClick={()=>start(Number(r.id))}>开始</Button> : null}
          {(s === 'in_progress' || s === 'accepted') ? <Button size="small" type="primary" onClick={()=>complete(Number(r.id))}>完成</Button> : null}
        </Space>
      );
    }},
  ], [warehouseOptions]);

  return (
    <div className="p-4">
      <Card title="仓内工单列表（Internal）" extra={<Space>
        <Select placeholder="状态" style={{ width: 140 }} allowClear value={filters.status} onChange={(v)=> setFilters(prev=>({ ...prev, status: v || undefined }))} options={[
          { value: 'pending', label: '待领取' },
          { value: 'accepted', label: '已领取' },
          { value: 'in_progress', label: '执行中' },
          { value: 'completed', label: '已完成' },
          { value: 'cancelled', label: '已取消' },
          { value: 'rejected', label: '已驳回' },
        ]} />
        <Select placeholder="仓库" style={{ width: 220 }} allowClear loading={whLoading} value={filters.warehouse_id as any} onChange={(v)=> setFilters(prev=>({ ...prev, warehouse_id: v || undefined }))} options={warehouseOptions} />
        <Select placeholder="优先级" style={{ width: 140 }} allowClear value={filters.priority} onChange={(v)=> setFilters(prev=>({ ...prev, priority: v || undefined }))} options={[
          { value: 'normal', label: '普通' },
          { value: 'high', label: '一般优先' },
          { value: 'super', label: '超级优先' },
        ]} />
        <Input placeholder="系统编码/备注 关键字" style={{ width: 220 }} value={filters.keyword} onChange={(e)=> setFilters(prev=>({ ...prev, keyword: e.target.value }))} />
        <Button onClick={()=> load()}>刷新</Button>
        <Button onClick={()=> setFilters({})}>重置</Button>
      </Space>}>
        <Table rowKey={(r:any)=> String(r.id)} loading={loading} dataSource={rows} columns={columns as any} pagination={{ pageSize: 10 }} />
      </Card>
    </div>
  );
};

export default InternalWorkOrderListPage;


